import { useState, useEffect, useRef } from 'react';
import type { AnalyticsPeriod, DateRange } from '@/types';

interface PeriodSelectorProps {
  value: AnalyticsPeriod;
  onChange: (period: AnalyticsPeriod) => void;
  customDateRange?: DateRange | null;
  onCustomDateRange?: (range: DateRange) => void;
}

const periods: { value: AnalyticsPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'mtd', label: 'Month to Date' },
  { value: '90d', label: '90 days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last_month', label: 'Last Month' },
  { value: '12m', label: '12 months' },
];

export function PeriodSelector({ value, onChange, customDateRange, onCustomDateRange }: PeriodSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = value === 'custom'
    ? null
    : periods.find((p) => p.value === value);

  const displayLabel = value === 'custom' && customDateRange
    ? `${customDateRange.start} - ${customDateRange.end}`
    : selectedOption?.label || 'Select period';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomApply = () => {
    if (customStart && customEnd && onCustomDateRange) {
      onCustomDateRange({ start: customStart, end: customEnd });
      setIsOpen(false);
      setShowCustom(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-900 flex items-center gap-2 hover:border-gray-300 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="max-w-[180px] truncate">{displayLabel}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px]">
          {periods.map((period) => (
            <div
              key={period.value}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                value === period.value
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => {
                onChange(period.value);
                setIsOpen(false);
                setShowCustom(false);
              }}
            >
              {period.label}
            </div>
          ))}
          <div className="border-t border-gray-100">
            <div
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                value === 'custom'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setShowCustom(!showCustom)}
            >
              Custom Range
            </div>
            {showCustom && (
              <div className="px-3 py-2 space-y-2 border-t border-gray-100">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Start</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">End</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded"
                  />
                </div>
                <button
                  onClick={handleCustomApply}
                  disabled={!customStart || !customEnd}
                  className="w-full py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PeriodSelector;
